import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import colors from '../theme/colors';
import { getFlaggedCards, resolveFlaggedCard } from '../services/adminApi';

/**
 * Admin-only list of cards flagged during the sources-first rewrite.
 *
 * A card is flagged when the evidence is thin, when two whitelisted sources contradict each
 * other, or - the most common case by far - when the node title promises something the sources
 * do not support. Those are editorial calls that only Romain can make, so the point of this
 * panel is to carry the reason and hand him straight to the card.
 *
 * Each row carries a Resolve button that clears the flag once the call has been made. It writes
 * to the production database directly, so a card leaves the list without a redeploy.
 *
 * Rendered only when isAdmin is true. The endpoint enforces that server-side as well.
 */

const TriggerBtn = styled.button`
  background: rgba(255, 193, 7, 0.14);
  border: 1px solid rgba(255, 193, 7, 0.45);
  border-radius: 20px;
  padding: 6px 14px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  margin-bottom: -12px;
  margin-left: 8px;

  &:hover {
    background: rgba(255, 193, 7, 0.24);
    border-color: rgba(255, 193, 7, 0.7);
  }
`;

const Count = styled.span`
  background: rgba(255, 193, 7, 0.9);
  color: #1a1a1a;
  border-radius: 10px;
  padding: 1px 7px;
  font-size: 0.72rem;
  font-weight: 800;
`;

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Modal = styled(motion.div)`
  background-color: ${colors.base.themePrimaryTransparent};
  background-image:
    linear-gradient(73deg,  transparent calc(47% - 1px), rgba(255,255,255,0.14) 47%, transparent calc(47% + 1px)),
    linear-gradient(161deg, transparent calc(38% - 1px), rgba(255,255,255,0.10) 38%, transparent calc(38% + 1px)),
    linear-gradient(210deg, transparent calc(55% - 1px), rgba(255,255,255,0.08) 55%, transparent calc(55% + 1px));
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 24px;
  box-shadow: 0 16px 60px rgba(0, 0, 0, 0.5);
  padding: 24px;
  width: min(760px, 100%);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 20px;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: #fff;
  font-weight: 700;
`;

const CloseBtn = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  color: #fff;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;

  &:hover { background: rgba(255, 255, 255, 0.2); }
`;

const Intro = styled.p`
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.72);
`;

const List = styled.div`
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 4px;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-left: 3px solid rgba(255, 193, 7, 0.8);
  border-radius: 12px;
  padding: 12px 14px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover { background: rgba(255, 255, 255, 0.13); }
`;

const CardTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`;

const CardTitle = styled.div`
  color: #fff;
  font-weight: 700;
  font-size: 0.92rem;
`;

const CardMeta = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.72rem;
  white-space: nowrap;
`;

const CardRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const ResolveBtn = styled.button`
  background: rgba(92, 165, 165, 0.18);
  border: 1px solid rgba(92, 165, 165, 0.5);
  border-radius: 14px;
  padding: 4px 11px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(92, 165, 165, 0.34);
    border-color: rgba(92, 165, 165, 0.85);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const RowError = styled.div`
  margin-top: 6px;
  color: rgba(255, 150, 150, 0.95);
  font-size: 0.74rem;
`;

const Reason = styled.div`
  margin-top: 6px;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.78rem;
  line-height: 1.5;
`;

const Empty = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  padding: 18px 0;
  text-align: center;
`;

const FlaggedCardsPopup = ({ isAdmin, onNavigateToNode }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState({});
  const [failed, setFailed] = useState({});

  // Fetched once the admin opens the panel rather than on mount: a non-admin never triggers the
  // request, and the list is only a few dozen rows so there is nothing to cache.
  useEffect(() => {
    if (!open || data) return;
    let cancelled = false;
    setLoading(true);
    getFlaggedCards().then(res => {
      if (!cancelled) { setData(res); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [open, data]);

  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!isAdmin) return null;

  const goTo = (id) => {
    setOpen(false);
    if (onNavigateToNode) onNavigateToNode(id);
  };

  // The row is dropped only once the server confirms, so a failed call leaves the card in place
  // with its reason still readable instead of making it vanish and reappear.
  const resolve = async (e, id) => {
    e.stopPropagation();
    if (resolving[id]) return;

    setResolving(r => ({ ...r, [id]: true }));
    setFailed(f => ({ ...f, [id]: false }));

    try {
      await resolveFlaggedCard(id);
      setData(d => ({ ...d, cards: (d?.cards || []).filter(c => c.id !== id) }));
    } catch (err) {
      setFailed(f => ({ ...f, [id]: true }));
    } finally {
      setResolving(r => {
        const next = { ...r };
        delete next[id];
        return next;
      });
    }
  };

  const cards = data?.cards || [];

  return (
    <>
      <TriggerBtn onClick={() => setOpen(true)} title={t('admin.flaggedTitle')}>
        ⚠️ {t('admin.flaggedButton')}
        {data && cards.length > 0 && <Count>{cards.length}</Count>}
      </TriggerBtn>

      {createPortal(
        <AnimatePresence>
          {open && (
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            >
              <Modal
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={e => e.stopPropagation()}
              >
                <ModalHeader>
                  <ModalTitle>⚠️ {t('admin.flaggedTitle')}</ModalTitle>
                  <CloseBtn onClick={() => setOpen(false)} aria-label={t('common.close')}>✕</CloseBtn>
                </ModalHeader>

                <Intro>{t('admin.flaggedIntro')}</Intro>

                <List>
                  {loading && <Empty>…</Empty>}

                  {!loading && data?.error && (
                    <Empty>{t('admin.flaggedError')} — {data.error}</Empty>
                  )}

                  {!loading && !data?.error && cards.length === 0 && (
                    <Empty>{t('admin.flaggedEmpty')}</Empty>
                  )}

                  {!loading && cards.map(c => (
                    <Card key={c.id} onClick={() => goTo(c.id)}>
                      <CardTop>
                        <CardTitle>{c.titleFr || c.titleEn}</CardTitle>
                        <CardRight>
                          <CardMeta>
                            {c.parentFr ? `${c.parentFr} · ` : ''}
                            {c.nSources != null ? `${c.nSources} src` : ''}
                          </CardMeta>
                          <ResolveBtn
                            onClick={e => resolve(e, c.id)}
                            disabled={!!resolving[c.id]}
                            title={t('admin.flaggedResolveHint')}
                          >
                            {resolving[c.id] ? '…' : `✓ ${t('admin.flaggedResolve')}`}
                          </ResolveBtn>
                        </CardRight>
                      </CardTop>
                      {c.reason && <Reason>{c.reason}</Reason>}
                      {failed[c.id] && <RowError>{t('admin.flaggedResolveFailed')}</RowError>}
                    </Card>
                  ))}
                </List>
              </Modal>
            </Overlay>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default FlaggedCardsPopup;
